import pluralize from 'pluralize'

import { IllustratedRow } from '@/components/Chat/ConfirmInfoCard'
import { IssueBanner } from '@/components/Chat/IssueBanner'
import { IllustrationName } from '@/components/icons/Illustration'
import { SkeletonRows } from '@/components/ui/skeleton'
import {
  collectLossRunClaims,
  countLossRunClaims,
  hasOutstandingLossRunRequest,
  useOrgLossRunRequests,
} from '@/models/lossRunRequest'

export const LOSS_RUNS_MISSING = 'Loss run reports not yet provided.'
export const LOSS_RUNS_REQUIRED = 'Insurance providers will require these before checkout.'
export const LOSS_RUNS_MORE =
  'Have more loss run reports? You can provide them after submitting your application.'

interface LossRunsState {
  isLoading: boolean
  label: string
  /** Nothing is on file or on its way in. */
  muted: boolean
  /** What the state costs at checkout, in an amber banner under the card's title like the card's checks. */
  warning?: string
  /** A small note under the card's content. */
  note?: string
}

function useLossRunsState(orgId: string): LossRunsState {
  const { lossRunRequests, documents, isParsing, isLoading } = useOrgLossRunRequests(orgId)
  // A row can stand for several claims, so total their counts rather than the rows.
  const claims = countLossRunClaims(collectLossRunClaims(lossRunRequests))
  // A request is only a report once one came back; an upload can be on file before it's read, or after a
  // read that failed, so "on file" claims nothing about what's in it.
  const onFile = documents.length > 0 || lossRunRequests.some((request) => request.receivedDocument)
  const state = { isLoading, muted: false }

  if (claims > 0)
    return { ...state, label: `${pluralize('claim', claims, true)} on file`, note: LOSS_RUNS_MORE }
  if (isParsing) {
    return { ...state, label: 'Updating your application with loss run info. This may take a few minutes...' }
  }
  if (onFile) return { ...state, label: 'Loss runs on file' }
  if (hasOutstandingLossRunRequest(lossRunRequests)) {
    return { ...state, label: 'Requested, not received yet', warning: LOSS_RUNS_REQUIRED }
  }
  return { ...state, label: LOSS_RUNS_MISSING, muted: true, warning: LOSS_RUNS_REQUIRED }
}

/** The Loss runs card while it's closed: what's on file, being read, or still asked for. */
export function LossRunsSummary({ orgId }: { orgId: string }) {
  const { isLoading, label, muted, warning, note } = useLossRunsState(orgId)
  if (isLoading) return <SkeletonRows rows={1} />

  return (
    <>
      <IssueBanner variant="warning" lines={warning ? [warning] : []} />
      <IllustratedRow illustration={IllustrationName.LOSS_RUN} label={label} muted={muted} />
      {note && <p className="text-muted-foreground text-xs">{note}</p>}
    </>
  )
}
